import React, {useState, useEffect} from 'react';
import { usePricingCategories, usePricingCategory } from '../../../Data/PricingCategories';
import { PageLoading } from '../../../Components/Loading';
import Section, { Sections } from '../../../Components/Containers/Section';
import Money from '../../../Components/Formatting/Money';
import Dropdown from '../../../Components/Dropdown';
import EditItem from './EditItem';
import EditCategory from './EditCategory';
import { useDialogues } from '../../../Components/Dialogues';
import NoItems from '../../../Components/EmptyStates/NoItems';

const Pricing = ({addPricingCallback}) => {
    const {categories, addCategory, editCategory, deleteCategory} = usePricingCategories();
    const [editingCategory, setEditingCategory] = useState(null);

    useEffect(() => {
        return addPricingCallback.listen(() => setEditingCategory({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>
        {categories ? <Inner {...{categories, setEditingCategory, deleteCategory}} /> : <PageLoading />}

        <EditCategory isOpen={editingCategory !== null}
            setIsOpen={() => setEditingCategory(null)}
            category={editingCategory}
            saveCategory={data => editingCategory.id ? editCategory(data) : addCategory(data)}
        />
    </>
}

const Inner = ({categories, setEditingCategory, deleteCategory}) => {
    return <Sections>
        {/* Make sure we re-render it if the name or description are changed */}
        {categories.map(category => <Category key={category.id+category.name+category.description}
            categoryId={category.id}
            setEditingCategory={setEditingCategory}
            deleteCategory={deleteCategory}
        />)}
        <NoItems icon="ListCheck"
            title="Add category"
            message={
                categories.length === 0 ?
                    'Create pricing categories and items that you can reuse to quickly create quotes and invoices.'
                    : null
            }
            onClick={() => setEditingCategory({})}
            short
        />
    </Sections>
}

const Category = ({categoryId, setEditingCategory, deleteCategory}) => {
    const {addItem, editItem, deleteItem, category} = usePricingCategory(categoryId);
    const [editingItem, setEditingItem] = useState(null);
    const {confirm} = useDialogues();

    return <Section title={category.name}
        className="pb-6 border-b-2"
        subtitle={category.description ?? 'No description'}
        menu={[
            {
                title: 'Add item',
                icon: 'Plus',
                onClick: () => setEditingItem({}),
            },
            {
                title: 'Edit category',
                icon: 'Pencil',
                onClick: () => setEditingCategory(category)
            },
            {
                div: true
            },
            {
                title: 'Delete category',
                icon: 'Trash',
                onClick: () => confirm(
                    {
                        theme: 'danger',
                        title: `Delete ${category.name}?`,
                        message: <>
                            Are you sure you want to delete this category?<br />
                            This won't affect any invoices that have already been created with it.
                        </>,
                        confirmLabel: 'Delete',
                    },
                    () => deleteCategory(category.id),
                )
            },
        ]}
    >
        <div className="flex flex-col divide-y divide-dashed">
            {category.items.map(item => <Item key={item.id} {...{item, setEditingItem, deleteItem}} />)}
        </div>
        <NoItems icon="Plus" title="Add item" onClick={() => setEditingItem({})} shorter />

        <EditItem isOpen={editingItem !== null}
            setIsOpen={() => setEditingItem(null)}
            item={editingItem}
            saveItem={data => editingItem.id ? editItem(data) : addItem(data)}
        />
    </Section>
}

const Item = ({item, setEditingItem, deleteItem}) => {
    const {confirm} = useDialogues();

    return <div className="flex justify-between items-center py-2">
        <div>
            <p>
                <strong>{item.name}</strong><br />
                <span className="text-muted">{item.description ?? 'No description'}</span>
            </p>
            <p className="mt-1">
                Priced as: {item.displayPrice}

                <span className="ml-2 text-muted">
                    {item.defaultUnitPrice !== null ? <Money pence={item.defaultUnitPrice} /> : '[ No price ]'}
                </span>
            </p>
        </div>
        <div>
            <Dropdown options={[
                {
                    title: 'Edit',
                    icon: 'Pencil',
                    onClick: () => setEditingItem(item),
                },
                {
                    title: 'Delete',
                    icon: 'Trash',
                    onClick: () => confirm(
                        {
                            theme: 'danger',
                            title: `Delete ${item.name}?`,
                            message: <>
                                Are you sure you want to delete this item?<br />
                                This won't affect any invoices that have already been created with it.
                            </>,
                            confirmLabel: 'Delete',
                        },
                        () => deleteItem(item.id),
                    )
                }
            ]} />
        </div>
    </div>
}

export default Pricing;